import React from 'react';
import { Mail, Phone, MapPin, Clock, Linkedin, FileText, Loader2 } from 'lucide-react';
import { useProfile } from '../hooks/usePortfolioData';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const Contact = () => {
  const { profile, loading } = useProfile();

  const workingHours = [
    { location: 'Saudi Arabia (KSA)', hours: 'Sunday - Thursday: 8:00 AM - 6:00 PM', timezone: 'AST (UTC+3)' },
    { location: 'Egypt (EGY)', hours: 'Sunday - Thursday: 9:00 AM - 7:00 PM', timezone: 'EET (UTC+2)' }
  ];

  const projectTypes = [
    'Retail & Commercial Fit-outs',
    'Corporate Office Design',
    'Restaurant & Hospitality',
    'Large-scale Retail Projects',
    'Technical Office Leadership',
    'Project Management Consulting'
  ];

  if (loading || !profile) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-gray-400" size={48} />
      </div>
    );
  }

  const personalInfo = profile;
  const phones = personalInfo.contact?.phones || [];
  const email = personalInfo.contact?.email || '';
  const linkedin = personalInfo.contact?.linkedin || '';

  const contactMethods = [
    {
      icon: Mail,
      title: 'Email',
      primary: email,
      secondary: 'Professional inquiries welcome',
      action: `mailto:${email}`,
      actionText: 'Send Email'
    },
    ...(phones[0] ? [{
      icon: Phone,
      title: 'Phone (Saudi Arabia)',
      primary: phones[0],
      secondary: 'Available during business hours',
      action: `tel:${phones[0]}`,
      actionText: 'Call Now'
    }] : []),
    ...(phones[1] ? [{
      icon: Phone,
      title: 'Phone (Egypt)',
      primary: phones[1],
      secondary: 'Available during business hours',
      action: `tel:${phones[1]}`,
      actionText: 'Call Now'
    }] : []),
    {
      icon: Linkedin,
      title: 'LinkedIn',
      primary: 'Professional Network',
      secondary: 'Connect for business opportunities',
      action: linkedin,
      actionText: 'Connect'
    }
  ];

  return (
    <div className="pt-20">
      {/* Header */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Get In Touch</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Ready to discuss your next architectural project? I'm available for consultations,
            project collaborations, and technical office leadership opportunities.
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactMethods.map((method, index) => {
              const IconComponent = method.icon;
              return (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="text-gray-600" size={32} />
                    </div>
                    <h3 className="font-semibold text-lg text-gray-900 mb-2">{method.title}</h3>
                    <p className="text-gray-900 font-medium mb-2">{method.primary}</p>
                    <p className="text-sm text-gray-600 mb-4">{method.secondary}</p>
                    <Button asChild size="sm" className="w-full">
                      <a href={method.action}>{method.actionText}</a>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Working Hours */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Working Hours</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {workingHours.map((schedule, index) => (
              <Card key={index}>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Clock className="text-gray-600" size={20} />
                    </div>
                    <h3 className="font-semibold text-lg text-gray-900">{schedule.location}</h3>
                  </div>
                  <p className="text-gray-700 mb-2">{schedule.hours}</p>
                  <p className="text-sm text-gray-500">{schedule.timezone}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Project Types */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Project Specializations</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projectTypes.map((type, index) => (
              <Card key={index} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <FileText className="text-gray-600" size={16} />
                    </div>
                    <span className="font-medium text-gray-900">{type}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Professional Details */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">{personalInfo.name}</h2>
            <p className="text-xl text-gray-300 mb-2">{personalInfo.title}</p>
            <p className="text-lg text-gray-400 mb-6">{personalInfo.subtitle}</p>
            <p className="text-gray-400">{personalInfo.nationality}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-xl font-semibold mb-4">Professional Summary</h3>
              <p className="text-gray-300 leading-relaxed mb-6">
                {personalInfo.bio}
              </p>
              <p className="text-gray-300 leading-relaxed">
                {personalInfo.mission}
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-4">Quick Links</h3>
              <div className="space-y-4">
                <Button variant="outline" size="lg" className="w-full border-white text-white hover:bg-white hover:text-gray-900" asChild>
                  <a href={linkedin} target="_blank" rel="noopener noreferrer">
                    <Linkedin className="mr-2" size={16} />
                    LinkedIn Profile
                  </a>
                </Button>
                <Button variant="outline" size="lg" className="w-full border-white text-white hover:bg-white hover:text-gray-900" asChild>
                  <a href="/documents/Omar_Mosallam_CV.pdf" download="Omar_Mosallam_CV.pdf" target="_blank">
                    <FileText className="mr-2" size={16} />
                    Download Resume
                  </a>
                </Button>
                <Button variant="outline" size="lg" className="w-full border-white text-white hover:bg-white hover:text-gray-900" asChild>
                  <a href={`mailto:${email}`}>
                    <Mail className="mr-2" size={16} />
                    Request Portfolio
                  </a>
                </Button>
              </div>
            </div>
          </div>

          <div className="text-center mt-12 pt-12 border-t border-gray-800">
            <p className="text-gray-400 mb-4">
              "I strive to deliver projects that combine design excellence with technical integrity,
              meeting the highest international standards while serving the unique needs of each client."
            </p>
            <p className="text-sm text-gray-500">- {personalInfo.name}</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
